import { DeleteOutlined, CheckOutlined } from "@ant-design/icons";
import { Descriptions, Flex, List, Badge, Collapse, Popconfirm } from "antd";
import classNames from "classnames";
import { groupBy } from "lodash-es";
import { ReactElement, ReactNode, HTMLAttributes, useMemo } from "react";

import styles from "./ConfigurationsList.module.scss";

import { ImportConfiguration } from "../../types/importConfiguration";
import { getDocumentSimilarity } from "../../utils/getDocumentSimilarity";
import { AddConfigurationModal } from "../AddConfigurationModal";
import { Description } from "../Description";
import { CustomButton } from "../FormFields/CustomButton";

const getRibbonColor = (similarity: number): string => {
  if (similarity > 0.8) {
    return "green";
  } else if (similarity > 0.5) {
    return "orange";
  } else {
    return "red";
  }
};

type ConfigurationsListProps = {
  importConfigurations: ImportConfiguration[];
  headerRow: number | null;
  dataRow: number | null;
  fileData: any; // Replace with a more specific type if available
  sheetName: string;
  handleAddConfiguration: (config: ImportConfiguration) => void;
  handleConfigurationDelete: (config: ImportConfiguration) => void;
  selectedImportConfiguration: ImportConfiguration | null;
  handleImportConfigurationSelect: (config: ImportConfiguration) => void;
};

export const ConfigurationsList = ({
  importConfigurations = [],
  headerRow,
  dataRow,
  fileData,
  sheetName,
  handleAddConfiguration,
  handleConfigurationDelete,
  selectedImportConfiguration,
  handleImportConfigurationSelect
}: ConfigurationsListProps): ReactElement => {
  const documentHeaders = useMemo(() => {
    if (!sheetName || !headerRow) return "";

    const sheet = fileData.Sheets[sheetName];
    const cells = Object.entries(sheet).filter(([key]) => !key.startsWith("!"));

    const groupedByRow = groupBy(cells, ([key]) => key.replace(/[A-Z]/g, ""));

    // @ts-ignore
    return groupedByRow[headerRow]?.map((c) => c[1].v).join(" ") || "";
  }, [fileData, sheetName, headerRow]);

  const configurationToString = (config: ImportConfiguration): string => {
    return config.importConfigurationFieldMappings
      .map(({ sourceFieldIdentifier }) => sourceFieldIdentifier)
      .join(" ");
  };

  const sortedConfigurations = useMemo(
    () =>
      importConfigurations
        .map((c) => ({
          ...c,
          similarity: getDocumentSimilarity(
            configurationToString(c),
            documentHeaders
          ),
        })),
        // .sort((a, b) => b.similarity - a.similarity),
    [importConfigurations, documentHeaders]
  );

  return (
    <div className={styles.root} id="configurations-list">
      <List
        footer={null}
        grid={{ gutter: 160 }}
        size="small"
        header={
          <>
            <Flex justify="space-between" align="center">
              {sortedConfigurations.length > 0
                ? <Description theme="t1">Configurations ({sortedConfigurations.length}):</Description>
                : <Description theme="t1">No configurations saved</Description>
              }
              <AddConfigurationModal
                action={handleAddConfiguration}
                sheetNames={fileData.SheetNames}
                sheetName={sheetName}
                headerRow={headerRow}
                dataRow={dataRow}
              />
            </Flex>
          </>}
      >
        {sortedConfigurations.map((config) => (
          <ConfigurationItem selected={config.id === selectedImportConfiguration?.id} match={config?.similarity}>
            <List.Item style={{ marginBlockEnd: 0 }}>
              <Flex
                gap={8}
                align="center"
                justify="flex-start"
              >
                <div className={classNames(styles.configurationItem_col, styles.configurationItem_col_select)}>
                  {config.id === selectedImportConfiguration?.id
                    ? <SelectedConfButton />
                    : <CustomButton
                        theme="secondary"
                        onClick={() => handleImportConfigurationSelect(config)}
                      >
                     Select
                    </CustomButton>}
                </div>
                <List.Item.Meta
                  className={styles.configurationItem_col}
                  description={<ConfiguationDescription config={config} similarity={config?.similarity}/>}
                />
                <Flex className={styles.configurationItem_col}>
                  <Popconfirm
                    title="Are you sure to delete this configuration?"
                    onConfirm={() => {handleConfigurationDelete(config)}}
                    okText="Yes"
                    cancelText="No"
                    placement="top"
                  >
                    <DeleteOutlined
                      className={styles.deleteIcon}
                    />
                  </Popconfirm>
                  
                </Flex>
              </Flex>
            </List.Item>
          </ConfigurationItem>
        ))} 
      </List>
    </div>
  );
};

type ConfigurationItemProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  match?: number;
  selected?: boolean;
};

const SelectedConfButton = () => {
  return (
    <div className={styles.selectedConfButton}>
      <CheckOutlined className={styles.selectedIcon} /> Selected
    </div>
  );
}

const ConfiguationDescription = ({ config, similarity }: {
  config: ImportConfiguration;
  similarity: number;
}) => {
  return (
    <Badge.Ribbon
      text={`${Math.round(similarity * 100)}% match`}
      color={getRibbonColor(similarity)}
      placement="end"
    >
      <Collapse
        bordered={false}
        items={[{
          key: "1",
          label: config.name,
          children: (
            <Descriptions
                size="middle"
                layout="vertical"
                // bordered
                column={4}
                items={[
                    {
                        key: 0,
                        label: "Sheet",
                        children: config.sourceIdentifier
                    },
                    {
                        key: 1,
                        label: "Report name",
                        children: config.destinationIdentifier
                    },
                    {
                        key: 2,
                        label: "Header row",
                        children: config.importConfigurationArguments?.find((a) => a.identifier === "HeaderRowIndex")?.value || "n/a"
                    },
                    {
                        key: 3,
                        label: "Data row",
                        children: config.importConfigurationArguments?.find((a) => a.identifier === "FirstDataRowIndex")?.value || "n/a"
                    }                    
                ]}
            />
          ),
        }]}
      />
    </Badge.Ribbon>
  )
}

const ConfigurationItem = ({ children, match = 0, selected, ...props }: ConfigurationItemProps) => {
  return (
      <div {...props} className={classNames(styles.configurationItem, props.className, { [styles.selected]: selected })}>
        {children}
      </div>
  );
};